import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams, Link } from 'react-router-dom'
import type { Entry } from '../api/entries'
import { getEntriesByTag } from '../api/tags'
import { EntryCard } from '../components/Entry/EntryCard'
import { EntryComposer } from '../components/Entry/EntryComposer'
import { useAuthorTheme } from '../hooks/useAuthorTheme'
import { useUsername } from '../hooks/useUsername'
import { useAuthStore } from '../store/auth'
import { usePageHeader } from '../hooks/usePageHeader'

export function TagView() {
  const username = useUsername()
  const currentUser = useAuthStore((s) => s.username)
  const { tagName } = useParams<{ tagName: string }>()
  const [searchParams] = useSearchParams()
  useAuthorTheme(username ?? undefined)
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [composerOpen, setComposerOpen] = useState(() => searchParams.get('compose') === '1')

  useEffect(() => {
    if (!username || !tagName) return
    setLoading(true)
    getEntriesByTag(username, tagName)
      .then(setEntries)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [username, tagName])

  const titleNode = useMemo(() => (
    username && tagName ? (
      <>
        <Link to={`/~${username}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{username}</Link>
        {' / '}
        <Link to={`/~${username}/tags`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>tags</Link>
        {` / #${tagName}`}
      </>
    ) : 'tag'
  ), [username, tagName])

  const tagActions = useMemo(() => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
      {username && tagName && (
        <a href={`/~${username}/tags/${tagName}.atom`} style={{ fontSize: '0.8rem', opacity: 0.5 }}>atom feed</a>
      )}
      {currentUser === username && (
        <button type="button" onClick={() => setComposerOpen((o) => !o)} style={{ width: 'auto', fontSize: '0.8rem' }}>
          {composerOpen ? 'cancel' : '+ new entry'}
        </button>
      )}
    </div>
  ), [username, tagName, currentUser, composerOpen])

  usePageHeader(titleNode, tagActions)

  return (
    <div>
      {currentUser === username && tagName && composerOpen && (
        <EntryComposer
          initialTags={[tagName]}
          onCreated={(e) => { setEntries((prev) => [e, ...prev]); setComposerOpen(false) }}
        />
      )}
      {loading ? (
        <p>loading…</p>
      ) : entries.length === 0 ? (
        <p style={{ opacity: 0.5 }}>no entries with this tag</p>
      ) : (
        entries.map((e) => (
          <EntryCard key={e.id} entry={e} />
        ))
      )}
    </div>
  )
}
